
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import axios, { AxiosResponse } from 'axios'
import Notification from '@/classes/Notification'
import { Model } from '@/models/Model'
import { Bid } from '@/models/Bid'
import User from '@/models/User'
import { Cargo } from '@/models/Cargo'
import { Offer } from '@/models/Offer'
import { Message } from '@/models/Message'
import { Transport } from '@/models/Transport'
import { Vessel } from '@/models/Vessel'
import { createModelFromResponse } from '@/utils/model'
import { Document } from '@/models/Document'
import TransportType from '@/enums/TransportType'
import Datepicker from '@/components/Datepicker.vue'
import DateTimePicker from '@/components/DateTimePicker.vue'
import TransportDocumentGenerator from '@/components/DocumentGenerators/TransportDocument.vue'
import AdvisingGenerator from '@/components/DocumentGenerators/Advising.vue'
import TransportLocationStatus from '@/enums/TransportLocationStatus'
import i18n from '@/i18n'
import { namespace } from 'vuex-class'
import HopperMeasurementsReport from '@/components/DocumentGenerators/HopperMeasurementsReportGenerator.vue'
import LCIReportGenerator from '@/components/DocumentGenerators/LCIReport.vue'
import { CertificationClaimType } from '@/models/CertificationClaimType'
import { CargoTerms } from '@/models/CargoTerms'
import FileTag from '@/enums/FileTag'
import { Invoice } from '@/models/accounting/Invoice'
import debounce from 'lodash.debounce'
import UnloadingBillDialog from '@/views/V3Wrappers/UnloadingBillDialog.vue'

export const supportedFileTypes = [
	'application/pdf',
	'application/vnd.ms-excel',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'text/xml',
	'text/csv',
	'text/plain',
	'image/x-png',
	'image/png',
	'image/gif',
	'image/jpeg',
	'application/vnd.ms-powerpoint',
	'application/zip'
].join(',')

export type AttachmentType = {
	category: 'general' | 'transport' | 'vessel'
	icon: string
	value: FileTag
	product_types?: Array<TransportType>
	generatable?: boolean
	// eslint-disable-next-line no-use-before-define
	isAllowed?: (vm: AddAttachment) => boolean
	// eslint-disable-next-line no-use-before-define
	isVisible?: (vm: AddAttachment) => boolean
	canExpire?: boolean
	required?: Array<{
		isLoadingLocation?: boolean
		status: TransportLocationStatus
	}>
}

export const FileTypes: Array<AttachmentType> = [
	{
		category: 'vessel',
		icon: 'attach_file',
		value: FileTag.BARGING_LETTER_FRONT,
		canExpire: true
	},
	{
		category: 'vessel',
		icon: 'attach_file',
		value: FileTag.BARGING_LETTER_REAR,
		canExpire: true
	},
	{
		category: 'vessel',
		icon: 'attach_file',
		value: FileTag.MEASUREMENT_CERTIFICATE,
		canExpire: true
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.NOMINATION,
		product_types: [TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.TIME_SHEET,
		product_types: [TransportType.LIQUID],
		required: [
			{
				isLoadingLocation: true,
				status: TransportLocationStatus.TRANSSHIPPING
			},
			{
				isLoadingLocation: true,
				status: TransportLocationStatus.FINISHED
			},
			{
				isLoadingLocation: false,
				status: TransportLocationStatus.TRANSSHIPPING
			},
			{
				isLoadingLocation: false,
				status: TransportLocationStatus.FINISHED
			}
		]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.TRANSPORT_ORDER,
		product_types: [TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.STOWAGE_PLAN_STABILITY_CALCULATION,
		product_types: [TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.LOADING_PLAN,
		product_types: [TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.DISCHARGE_PLAN,
		product_types: [TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.DECLARATION_OF_RELEASE,
		product_types: [TransportType.LIQUID],
		required: [
			{
				isLoadingLocation: false,
				status: TransportLocationStatus.TRANSSHIPPING
			},
			{
				isLoadingLocation: false,
				status: TransportLocationStatus.FINISHED
			}
		]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.ADN_CHECKLIST,
		product_types: [TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.VOYAGE_PREPARATION,
		product_types: [TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.VESSEL_DATA,
		product_types: [TransportType.LIQUID],
		required: [
			{
				isLoadingLocation: true,
				status: TransportLocationStatus.TRANSSHIPPING
			},
			{
				isLoadingLocation: true,
				status: TransportLocationStatus.FINISHED
			},
			{
				isLoadingLocation: false,
				status: TransportLocationStatus.ARRIVED
			}
		]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.LETTER_OF_PROTEST,
		product_types: [TransportType.LIQUID],
		required: [
			{
				isLoadingLocation: true,
				status: TransportLocationStatus.TRANSSHIPPING
			},
			{
				isLoadingLocation: true,
				status: TransportLocationStatus.FINISHED
			},
			{
				isLoadingLocation: false,
				status: TransportLocationStatus.TRANSSHIPPING
			},
			{
				isLoadingLocation: false,
				status: TransportLocationStatus.FINISHED
			}
		]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.CUSTOMS_DOCUMENT,
		product_types: [TransportType.LIQUID],
		required: [
			{
				isLoadingLocation: true,
				status: TransportLocationStatus.TRANSSHIPPING
			},
			{
				isLoadingLocation: true,
				status: TransportLocationStatus.FINISHED
			}
		]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.TEMPERATURE_REGISTRATION,
		product_types: [TransportType.LIQUID],
		required: [
			{
				isLoadingLocation: false,
				status: TransportLocationStatus.ARRIVED
			}
		]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.EMPTY_VESSEL_TRANSPORT_DOCUMENT,
		product_types: [TransportType.LIQUID],
		required: [
			{
				isLoadingLocation: true,
				status: TransportLocationStatus.TRANSSHIPPING
			},
			{
				isLoadingLocation: true,
				status: TransportLocationStatus.FINISHED
			},
			{
				isLoadingLocation: false,
				status: TransportLocationStatus.TRANSSHIPPING
			},
			{
				isLoadingLocation: false,
				status: TransportLocationStatus.FINISHED
			}
		]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.LOADING_DOCUMENT,
		product_types: [TransportType.BULK, TransportType.CONTAINER, TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.UNLOADING_DOCUMENT,
		product_types: [TransportType.BULK, TransportType.CONTAINER, TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.BILL_OF_LADING,
		product_types: [TransportType.BULK, TransportType.CONTAINER, TransportType.LIQUID],
		generatable: true,
		isAllowed: (vm) => vm.userHasBillOfLadingTransportMembership
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.BUILDING_MATERIALS,
		product_types: [TransportType.BULK],
		generatable: true,
		isAllowed: (vm) => !!vm.transport?.generateBillOfLading
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.ADVISING,
		product_types: [TransportType.BULK, TransportType.LIQUID],
		generatable: true,
		isAllowed: (vm) => vm.userHasAdvisingDocumentMembership
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.HOPPER_MEASUREMENT,
		product_types: [TransportType.BULK],
		generatable: true,
		isAllowed: (vm) => !!vm.transport?.generateHopperMeasurementsReport
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.LCI_REPORT,
		product_types: [TransportType.BULK, TransportType.CONTAINER, TransportType.LIQUID],
		generatable: true,
		isAllowed: (vm) => vm.userHasLCIMembership && vm.hasGMPCertification,
		isVisible: (vm) => vm.hasGMPCertification
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.INVOICE,
		product_types: [TransportType.BULK, TransportType.CONTAINER, TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.CONTAINER_MANIFEST,
		product_types: [TransportType.BULK, TransportType.CONTAINER]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.CHLORIDE_INSPECTION_REPORT,
		product_types: [TransportType.BULK]
	},
	{
		category: 'general',
		icon: 'attach_file',
		value: FileTag.OTHER,
		product_types: [TransportType.BULK, TransportType.CONTAINER, TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.PURCHASE_ORDER,
		product_types: [TransportType.BULK, TransportType.CONTAINER, TransportType.LIQUID]
	},
	{
		category: 'transport',
		icon: 'attach_file',
		value: FileTag.PRODUCT_ANALYSIS,
		product_types: [TransportType.BULK, TransportType.CONTAINER, TransportType.LIQUID]
	}
]

export const documentGenerators = [
	FileTag.HOPPER_MEASUREMENT,
	FileTag.BUILDING_MATERIALS,
	FileTag.LCI_REPORT,
	FileTag.ADVISING,
	FileTag.BILL_OF_LADING
]

export const isDocumentGenerator = (fileTag: FileTag) => documentGenerators.includes(fileTag)

const Auth = namespace('auth')

@Component({
	computed: {
		FileTag() {
			return FileTag
		}
	},
	components: {
		UnloadingBillDialog,
		HopperMeasurementsReport,
		Datepicker,
		DateTimePicker,
		TransportDocumentGenerator,
		AdvisingGenerator,
		LCIReportGenerator
	}
})
export default class AddAttachment extends Vue {
	@Prop({ required: true })
	value!: boolean

	@Prop({ default: null })
	showGenerator!: FileTag | null

	@Prop({ required: false, default: 'transport' })
	category!: 'vessel' | 'transport'

	@Prop({ default: null })
	transport!: Transport | null

	@Prop({ default: null })
	bid!: Bid | null

	@Prop({ default: null })
	cargo!: Cargo | null

	@Prop({ default: null })
	offer!: Offer | null

	@Prop({ default: null })
	message!: Message | null

	@Prop({ default: null })
	vessel!: Vessel | null

	@Prop({ default: null })
	invoice!: Invoice | null

	@Prop({ default: false })
	supportDragDrop!: boolean

	@Prop({ default: null })
	fileTag!: FileTag | null

	@Prop({ default: true })
	permanentDropZone!: boolean

	@Auth.Getter('hasBillOfLadingTransportMembership')
	userHasBillOfLadingTransportMembership!: boolean

	@Auth.Getter('hasAdvisingDocumentMembership')
	userHasAdvisingDocumentMembership!: boolean

	@Auth.Getter('hasLCIMembership')
	userHasLCIMembership!: boolean

	certificationClaimTypes: Array<CertificationClaimType> = []

	user!: User
	tag: AttachmentType | null = null
	canExpire: boolean = false
	showUploadSheet: boolean = false
	showProgressSheet: boolean = false
	showGenerateOption: boolean = false
	documentCreatorOpen: boolean = false
	isV3Generator: boolean = true

	transportType: TransportType = TransportType.BULK

	tags = FileTypes

	charterTerms: CargoTerms | null = null
	orderTerms: CargoTerms | null = null
	loading: boolean = false
	loaded: boolean = false
	dropHighlight: boolean = false
	droppedFile: File | null = null

	debouncedScheduledHide

	async created() {
		this.debouncedScheduledHide = debounce(this.scheduleHide, 1000)
		if (this.fileTag) {
			this.tag = this.tags.find((t) => t.value === this.fileTag)!
		}

		if (this.cargo) {
			this.transportType = this.cargo.transport
		}
		this.user = await User.fetchMeLazy()
	}

	get hasGMPCertification() {
		return !!(this.charterTerms ?? this.orderTerms)
			?.getCertificationClaimTypes()
			?.find((c) => c.slug === 'gmp_ovocom')
	}

	get filteredTags() {
		return this.tags
			.filter((type) => ['general', this.category].includes(type.category))
			.filter((tag) => this.category !== 'transport' || (tag.product_types ?? []).includes(this.transportType))
			.filter((tag) => (tag.isVisible ? tag.isVisible(this) : true))
			.map((tag) => {
				return {
					label: i18n.t(`cargo.attachments.types.${tag.value}`) as string,
					...tag
				}
			})
			.sort((tag, next) => {
				if (tag.value === 'other') return 1
				if (next.value === 'other') return -1
				return tag.label.localeCompare(next.label)
			})
	}

	async chooseAction(tag: AttachmentType) {
		this.tag = tag
		this.canExpire = tag.canExpire ?? false

		// chosen file tag after drop, upload dropped file
		if (this.droppedFile) {
			await this.uploadFile(this.droppedFile)
			this.showTypeSheet = false
			return
		}

		this.showTypeSheet = false

		if (tag.generatable && this.transport && tag.isAllowed && tag.isAllowed(this)) {
			this.showGenerateOption = true
			return
		}

		if (this.isMobileOS) {
			this.showUploadSheet = true
		} else {
			return this.showUpload()
		}
	}

	@Watch('showGenerator', { immediate: true })
	onShowGenerator() {
		if (this.showGenerator && isDocumentGenerator(this.showGenerator)) {
			this.documentCreatorOpen = true
			this.tag = this.tags.find((t) => t.value === this.showGenerator)!
		}
	}

	showCamera() {
		;(this.$refs.uploadImageInput as HTMLInputElement).click()
		this.showGenerateOption = false
	}

	showUpload() {
		;(this.$refs.uploadDocumentInput as HTMLInputElement).click()
		this.showGenerateOption = false
	}

	async showGenerateForm() {
		if (this.tag?.generatable && this.tag?.isAllowed && this.tag?.isAllowed(this)) {
			this.documentCreatorOpen = true
		}
		this.showGenerateOption = false
	}

	closeDocumentCreator() {
		this.documentCreatorOpen = false
		this.$emit('fileUploaded')
		this.$emit('closeGenerator')
	}

	openBolGenerator() {
		this.$emit('refreshTransport')
		this.tag = this.tags.find((t) => t.value === FileTag.BUILDING_MATERIALS)!
	}

	async uploadFile(file?: File) {
		if (!file) {
			return
		}

		const formData = new FormData()

		formData.append('tag', this.tag?.value ?? FileTag.OTHER)
		formData.append('name', file.name)
		if (this.user) {
			formData.append('toUser', String(this.user.getApiId()))
		}

		if (this.transport) {
			formData.append('relationships[transports][data][id]', String(this.transport.getApiId()))
		}

		if (this.bid) {
			formData.append('relationships[bids][data][id]', String(this.bid.getApiId()))
		}

		if (this.cargo) {
			formData.append('relationships[cargos][data][id]', String(this.cargo.getApiId()))
		}

		if (this.offer) {
			formData.append('relationships[offers][data][id]', String(this.offer.getApiId()))
		}

		if (this.message) {
			formData.append('relationships[messages][data][id]', String(this.message.getApiId()))
		}

		if (this.vessel && !this.transport) {
			formData.append('relationships[vessels][data][id]', String(this.vessel.getApiId()))
		}

		if (this.invoice) {
			formData.append('relationships[invoice][data][id]', String(this.invoice.getApiId()))
		}

		this.showUploadSheet = false
		this.showProgressSheet = true

		try {
			const response = (await axios.get(`${Model.baseUrl()}documents/signed-storage-url`)) as AxiosResponse<any>

			const returnedUuid = response.data.uuid
			const returnedUrl = response.data.url

			try {
				// call S3 url with the file PUT METHOD
				await axios.put(returnedUrl, file, {
					transformRequest: [
						(data, headers) => {
							delete headers.Authorization
							return data
						}
					]
				})

				// perform the request to backend with uuid and formData
				formData.append('file', returnedUuid)
				const documentResponse = await axios.post(`${Model.baseUrl()}documents`, formData)

				if (documentResponse) {
					await Notification.Success(i18n.t('base.file_uploaded'))
				}

				this.$emit(
					'fileUploaded',
					createModelFromResponse(Document, documentResponse.data) as Document,
					this.tag
				)
			} catch (err) {
				// eslint-disable-next-line
				console.error('Error occurred while uploading the file', err)
				await Notification.Error(i18n.t('errors.upload_errors').toString())
			}
		} finally {
			this.showProgressSheet = false
			;(this.$refs.uploadImageInput as HTMLInputElement).value = ''
			;(this.$refs.uploadDocumentInput as HTMLInputElement).value = ''
		}
	}

	get showTypeSheet() {
		return this.value
	}

	set showTypeSheet(value) {
		this.$emit('input', value)
	}

	@Watch('showTypeSheet')
	resetDroppedState(value) {
		if (!value) {
			this.droppedFile = null
			this.dropHighlight = false
		}
	}

	@Watch('value')
	async loadAdditionalData(value) {
		if (value && !this.loading && !this.loaded) {
			this.loading = true
			try {
				if (this.transport) {
					const cargo = await this.transport.fetchCargoLazy()
					this.charterTerms = cargo.getCharterTerms()
					this.orderTerms = cargo.getOrderTerms()
				}
				this.loaded = true
			} finally {
				this.loading = false
			}
		}
	}

	get isMobileOS() {
		const userAgent =
			// @ts-ignore
			navigator.userAgent || navigator.vendor || window.opera

		if (/android/i.test(userAgent)) {
			return true
		}

		// @ts-ignore
		if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
			return true
		}
		return false
	}

	async uploadViaDragAndDrop(droppedFile) {
		this.dropHighlight = false
		const firstFile = droppedFile.dataTransfer.files[0]
		if (!firstFile) return
		if (!this.fileTag) {
			this.droppedFile = firstFile
			this.showTypeSheet = true
		} else {
			await this.uploadFile(firstFile)
		}
	}

	dragLeave(evt) {
		if (evt.target === this.$refs.dropZone) {
			this.dropHighlight = false
		}
	}

	scheduleHide() {
		this.dropHighlight = false
	}

	get supportedFileTypes() {
		return supportedFileTypes
	}
}
